import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

/**
 * Protected Route for Technical Documentation
 *
 * Ensures only authenticated users with @videxa.co email domain
 * can access the developer documentation.
 *
 * Access Control:
 * - Unauthenticated users → Redirect to /login
 * - Non-videxa.co users → Redirect to /dashboard with accessDenied state
 * - @videxa.co users → Allow access
 */
const TechnicalDocsProtectedRoute = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // Check if user has @videxa.co domain
  const isVidexaDomain = user?.email?.toLowerCase().endsWith('@videxa.co');

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <ArrowPathIcon className="h-12 w-12 animate-spin mx-auto text-[#0E3056] dark:text-[#FFC000]" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Store the intended destination for post-login redirect
    sessionStorage.setItem('redirectAfterLogin', location.pathname);
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect non-videxa.co users to dashboard with access denied message
  if (!isVidexaDomain) {
    return (
      <Navigate
        to="/dashboard"
        state={{
          accessDenied: true,
          resource: 'technical-docs',
          message: 'Technical documentation is only accessible to Videxa employees.'
        }}
        replace
      />
    );
  }

  // User is authenticated and has @videxa.co domain - allow access
  return children;
};

export default TechnicalDocsProtectedRoute;
